import {config} from 'config/env';
import {SubDistrictStatusType} from 'constants/index';
import {getStoreAreasV2} from 'services/location/location-service';
import {getStoreAreasV1} from 'services/user/user-service';
import {CurrentStoreType} from 'store/types/app';
import {
  Address,
  AddressAreaLocale,
  CreateAddressInputType,
  StoreArea,
  TPinMapSelectedLocation,
  UserAddress,
  UserAddressV2
} from 'types';

interface IFilterStoreArea {
  data: StoreArea[];
  z: string[];
  pIDs: string[];
  dIDs: string[];
  sIDs: string[];
}

export const getStringFormOfAddress = (address: UserAddress, isThai = false, moveZipToLast = false) => {
  return [
    !moveZipToLast ? address?.zip : undefined,
    address?.company,
    address?.address1,
    address?.address2,
    address?.subDistrict?.[isThai ? 'nameTh' : 'nameEn'],
    address?.district?.[isThai ? 'nameTh' : 'nameEn'],
    address?.province?.[isThai ? 'nameTh' : 'nameEn'],
    moveZipToLast ? address?.zip : undefined
  ]
    .filter(Boolean)
    .join(', ');
};

export function mapUserAddressToShippingAddress(address: UserAddress, isThai: boolean): Address {
  const {__typename: _a, ...provinceWithLocale} = address.province;
  const {__typename: _b, ...districtWithLocale} = address.district;
  const {__typename: _c, ...subDistrictWithLocale} = address.subDistrict as AddressAreaLocale;

  const {latitude, longitude} = getCheckoutLatLng(address);

  const convertedAddress = {
    id: address?.id,
    address1: address?.address1,
    address2: address.address2,
    province: isThai ? address.province?.nameTh : address.province?.nameEn,
    provinceCode: address.province?.id,
    city: isThai ? address.district?.nameTh : address.district?.nameEn,
    company: address.company,
    country: address.country,
    countryCode: '',
    firstName: address.firstName,
    lastName: address.lastName,
    phone: address.mobile,
    zip: address.zip,
    subdistrict: isThai ? address.subDistrict?.nameTh : address?.subDistrict?.nameEn || '',
    latitude,
    longitude,
    taxId: address?.taxId || '',
    branchId: address?.branchId || '',
    provinceWithLocale,
    districtWithLocale,
    subDistrictWithLocale,
    metadata: address?.metadata
  };

  return convertedAddress;
}

export const getAllKysInFilterArea = (data: StoreArea[], k: keyof StoreArea) =>
  Array.from(new Set(data?.map((i) => i[k])).keys());

export const filterStoreArea = (storeArea: StoreArea[], filter?: Partial<StoreArea>): IFilterStoreArea => {
  const data = storeArea.filter((i) => {
    return filter
      ? Object.entries(filter).reduce((acc, [k, v]) => {
          if (v && i[k as keyof StoreArea] !== v) {
            acc = false;
          }
          return acc;
        }, true)
      : i;
  });
  return {
    data,
    z: getAllKysInFilterArea(data, 'z'),
    pIDs: getAllKysInFilterArea(data, 'pID'),
    dIDs: getAllKysInFilterArea(data, 'dID'),
    sIDs: getAllKysInFilterArea(data, 'sID')
  };
};

export const getMetaData = async (zip?: string) => {
  let data: StoreArea[] = [];
  try {
    if (config.IS_LOCATION_SVC_ENABLED && zip) {
      data = await getStoreAreasV2(zip);
    } else {
      data = await getStoreAreasV1({
        // ['status']: StatusEnum.DELIVERABLE
      });
    }
  } catch (error: any) {
    console.log('error', error.message);
  }
  return data;
};

export const isMatchExist = (array: string[], value: string) => array.includes(value);

export const assignDefaults = async ({
  val: _v,
  curr,
  isThai,
  locationsV2,
  selectedLocation,
  hasUserMovedMapManually,
  isExpress
}: {
  val: Partial<CreateAddressInputType>;
  curr?: CurrentStoreType;
  isThai?: boolean;
  locationsV2?: StoreArea[];
  selectedLocation: TPinMapSelectedLocation;
  hasUserMovedMapManually: boolean;
  isExpress?: boolean;
}) => {
  const fallbackZip = _v.zip || curr?.zipcode;
  const zipToUse =
    hasUserMovedMapManually && (!isExpress || config.ENABLE_UNIFY_BASKET)
      ? selectedLocation?.postalCode || fallbackZip
      : fallbackZip;

  let data;

  if (config.IS_LOCATION_SVC_ENABLED) {
    data = await getMetaData(zipToUse);
  } else {
    data = locationsV2?.length ? locationsV2 : await getMetaData();
  }

  const v = {..._v};

  if (!hasUserMovedMapManually) {
    return {v, data};
  }

  if (selectedLocation && (!isExpress || config.ENABLE_UNIFY_BASKET)) {
    v.zip = selectedLocation.postalCode;
    // matching if the subdistrict id is not there
    if (!v.subDistrict_id && selectedLocation.subdistrict && (!isExpress || config.ENABLE_UNIFY_BASKET)) {
      const currentSubdistrictValue = normalizeSubdistrictName(selectedLocation.subdistrict);
      if (currentSubdistrictValue) {
        const foundSubdistrict = data?.find((record) => isSubdistrictMatch(record.se, currentSubdistrictValue));
        if (foundSubdistrict) {
          v.subDistrict_id = foundSubdistrict.sID;
          v.district_id = foundSubdistrict.dID;
          v.zip = foundSubdistrict.z;
          v.province_id = foundSubdistrict.pID;
        }
      }
    }
    // if fail to find subdistrict, try to find district
    if (!v.district_id && selectedLocation.district && (!isExpress || config.ENABLE_UNIFY_BASKET)) {
      const currentDistrictValue = normalizeDistrictName(selectedLocation.district);
      const foundDistrict = data?.find((record) =>
        isMatchExist([record.d, record.de, record?.dGN], currentDistrictValue)
      );
      if (foundDistrict) {
        v.district_id = foundDistrict.dID;
        v.zip = foundDistrict.z;
        v.province_id = foundDistrict.pID;
      }
    }
    // if fail to find district, try to find province_id
    if (!v.province_id && selectedLocation.province && (!isExpress || config.ENABLE_UNIFY_BASKET)) {
      const currentProvinceValue = normalizeProvinceName(selectedLocation.province);
      const foundProvince = data?.find((record) =>
        isMatchExist([record.p, record.pe, record?.pGN], currentProvinceValue)
      );
      if (foundProvince) {
        v.province_id = foundProvince.pID;
        v.zip = foundProvince.z;
      }
    }
  }

  const hasVal = v.zip && (v.province_id || v.district_id || v.subDistrict_id);
  const getFilter = (_isThai?: boolean) => {
    const _filters = {} as StoreArea;
    if (hasVal) {
      v.zip && (_filters.z = v.zip);
      v.province_id && (_filters.pID = v.province_id);
      v.district_id && (_filters.dID = v.district_id);
      v.subDistrict_id && (_filters.sID = v.subDistrict_id);
    } else {
      curr?.zipcode && (_filters.z = curr?.zipcode);
      curr?.subdistrict && (_filters[_isThai ? 's' : 'se'] = curr?.subdistrict);
    }
    return _filters;
  };
  let filters = getFilter(isThai);
  let store = filterStoreArea(data, filters).data?.[0];
  if (!store) {
    filters = getFilter(!isThai);
    store = filterStoreArea(data, filters).data?.[0];
  }
  if (store) {
    v.zip || (v.zip = store.z);
    v.province_id || (v.province_id = store.pID);
    v.provinceName || (v.provinceName = store[isThai ? 'p' : 'pe']);
    v.subDistrict_id || (v.subDistrict_id = store.sID);
    v.subDistrictName || (v.subDistrictName = store[isThai ? 's' : 'se']);
    v.district_id || (v.district_id = store.dID);
    v.districtName || (v.districtName = store[isThai ? 'd' : 'de']);
  }
  return {
    v,
    data,
    store
  };
};

export const trimValues = (values: CreateAddressInputType): CreateAddressInputType => {
  const trimmedValues: CreateAddressInputType = {...values};

  (Object.keys(values) as (keyof CreateAddressInputType)[]).forEach((key) => {
    const value = values[key];
    if (typeof value === 'string' && value) {
      (trimmedValues[key] as unknown as string) = value.trim();
    }
  });

  return trimmedValues;
};

export const getAddressString = (
  storeArea: StoreArea[],
  province_id: string,
  district_id: string,
  subDistrict_id?: string,
  zip?: string | number,
  address1?: string,
  address2?: string
) => {
  if (province_id && district_id) {
    const province = storeArea.find(({pID}) => pID === province_id);
    const subDistrict = storeArea.find(({sID}) => sID === subDistrict_id);
    const district = storeArea.find(({dID}) => dID === district_id);
    const _addressRegion = `${subDistrict ? `${subDistrict.se}, ` : ''}${district ? `${district.de}, ` : ''}${
      province ? `${province.pe}, ` : ''
    }${zip ? `${zip}` : ''}`;

    return `${address1 ? `${address1}, ` : ''}${address2 ? `${address2}, ` : ''}${_addressRegion}`;
  }
};

export const setDefaults = ({pIDs, dIDs, sIDs}: Partial<IFilterStoreArea>, setFieldValue: any) => {
  pIDs && setFieldValue('province_id', pIDs.length === 1 ? pIDs[0] : null);
  dIDs && setFieldValue('district_id', dIDs.length === 1 ? dIDs[0] : null);
  sIDs && setFieldValue('subDistrict_id', sIDs.length === 1 ? sIDs[0] : null);
};

export const findAvailableIds = (data: IFilterStoreArea) => {
  const availableData = data.data.filter((elm) => elm.status === SubDistrictStatusType.DELIVERABLE);
  if (availableData.length) {
    const {pID, dID, sID} = availableData[0];
    return {
      pIDs: [pID],
      dIDs: [dID],
      sIDs: [sID]
    };
  }
  return data;
};

export const projectFilteredArea = (filteredStoreArea: StoreArea[], projection: keyof StoreArea) => {
  return Object.values(
    filteredStoreArea.reduce((acc, i) => {
      acc[i[projection]] = i;
      return acc;
    }, {} as any)
  ) as StoreArea[];
};

export const getAddressStringForGeocoding = (address: any) => {
  const address1 = address?.address1;
  const address2 = address?.address2;
  const zip = address?.zip;

  const subDistrict = address?.subDistrictName || address?.subDistrict?.nameEn;
  const district = address?.districtName || address?.district?.nameEn;
  const province = address?.provinceName || address?.province?.nameEn;

  return [address1, address2, subDistrict, district, province, zip]
    .filter((part) => part && part.trim() !== '')
    .join(', ');
};

export const convertUserAddressV2ToUserAddress = (addressV2: UserAddressV2): UserAddress => {
  return {
    ...addressV2,
    id: addressV2.addressId
  };
};

export const getCheckoutLatLng = (address: UserAddress): {latitude: number; longitude: number} => {
  const distanceThreshold = config.PIN_DISTANCE_THRESHOLD;

  const isUserPreferred = address?.metadata && address?.metadata?.distanceKm > distanceThreshold;
  const isLocusPreferred = address?.metadata && address?.metadata?.locusDistanceKm < distanceThreshold;
  const isLocusLatLngPreferred = config.IS_ENABLE_LOCUS_LAT_LONG && isLocusPreferred;

  const latitude =
    isLocusLatLngPreferred && address?.locus_latitude
      ? address?.locus_latitude
      : isUserPreferred && address?.latitude
        ? address?.latitude
        : (address?.user_latitude ?? 0);

  const longitude =
    isLocusLatLngPreferred && address?.locus_longitude
      ? address?.locus_longitude
      : isUserPreferred && address?.longitude
        ? address?.longitude
        : (address?.user_longitude ?? 0);

  return {latitude, longitude};
};

export const checkPlusCodeInAddress = (address: string) => {
  const plusCodeRegex = /\b(?:[23456789CFGHJMPQRVWX]{4,8}\+[\w]{2,3})\b/i;
  return plusCodeRegex.test(address);
};

export const normalizeSubdistrictName = (name: string) => {
  return name.replace(/(Khwaeng|Khwang|Tambon)/i, '').trim();
};

export const normalizeDistrictName = (name: string) => {
  return name.replace(/(Amphoe|Khet)/i, '').trim();
};

export const normalizeProvinceName = (name: string) => {
  return name.replace(/(Changwat)/i, '').trim();
};

export const isSubdistrictMatch = (value1: string, value2: string) => {
  const normalizedValue1 = normalizeSubdistrictName(value1).toLowerCase();
  const normalizedValue2 = normalizeSubdistrictName(value2).toLowerCase();
  if (normalizedValue1 === normalizedValue2) {
    return true;
  }
  for (const subdistrictAliasSet of config.SUBDISTRICT_ALIAS_SETS) {
    let value1Found = false;
    let value2Found = false;
    for (const alias of subdistrictAliasSet) {
      const aliasLowerCase = alias.toLowerCase();
      if (aliasLowerCase === normalizedValue1) {
        value1Found = true;
      }
      if (aliasLowerCase === normalizedValue2) {
        value2Found = true;
      }
    }
    if (value1Found && value2Found) {
      return true;
    }
  }
  return false;
};

export const isDistrictMatch = (value1: string, value2: string) => {
  const normalizedValue1 = normalizeDistrictName(value1).toLowerCase();
  const normalizedValue2 = normalizeDistrictName(value2).toLowerCase();
  return normalizedValue1 === normalizedValue2;
};

export function removeEmojisOnly(input: string): string {
  return input.replace(/\p{Extended_Pictographic}/gu, '');
}

export function sanitizeAddress<T extends CreateAddressInputType | Address>(input: T): T {
  const fieldsToSanitize: (keyof T)[] = [
    'address1',
    'address2',
    'branchId',
    'company',
    'country',
    'firstName',
    'lastName',
    'city'
  ] as (keyof T)[];

  const sanitized: Partial<T> = {};

  (Object.keys(input) as (keyof T)[]).forEach((key) => {
    const value = input[key];

    if (fieldsToSanitize.includes(key) && typeof value === 'string') {
      sanitized[key] = removeEmojisOnly(value).trim() as unknown as T[keyof T];
    } else {
      sanitized[key] = value;
    }
  });

  return sanitized as T;
}
